<template>
  <div class="mmg-storefront mmg-product-detail fusion-body">
    <div id="wrapper">
      <SiteHeader />

      <div
        class="fusion-page-title-bar fusion-page-title-bar-none fusion-page-title-bar-left"
      >
        <div class="fusion-page-title-row">
          <div class="fusion-page-title-wrapper">
            <div class="fusion-page-title-captions">
              <h1 class="entry-title">{{ productDisplayName || $t('common.product') }}</h1>
            </div>
            <div class="fusion-page-title-secondary">
              <div class="fusion-breadcrumbs">
                <router-link to="/">{{ $t('common.home') }}</router-link>
                <span class="fusion-breadcrumb-sep">/</span>
                <router-link to="/products">{{ $t('products.title') }}</router-link>
                <span class="fusion-breadcrumb-sep">/</span>
                <span class="breadcrumb-leaf">{{ productDisplayName || $t('common.detail') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main id="main" role="main" class="clearfix width-100">
        <div class="fusion-row product-detail-row">
          <section class="full-width product-detail-content">
            <p v-if="loading" class="product-detail-status">{{ $t('productDetail.loading') }}</p>
            <p v-else-if="loadError" class="product-detail-status">{{ loadError }}</p>

            <article v-else-if="product" class="product-detail-article">
              <div class="product-detail-layout">
                <div class="product-detail-media">
                  <div
                    v-if="product.galleryImages.length"
                    class="product-gallery"
                    role="region"
                    :aria-label="$t('common.productImages')"
                  >
                    <button
                      type="button"
                      class="product-gallery__main"
                      :aria-label="$t('common.previewImage')"
                      @click="openPreview"
                    >
                      <img
                        :src="activeImageSrc"
                        :alt="galleryAlt(activeImageIndex)"
                        class="product-gallery__main-img"
                      />
                      <span class="product-gallery__zoom" aria-hidden="true" />
                    </button>

                    <div
                      v-if="product.galleryImages.length > 1"
                      class="product-gallery__thumbs-bar"
                    >
                      <button
                        v-if="thumbsNeedNav"
                        type="button"
                        class="product-gallery__thumbs-nav product-gallery__thumbs-nav--prev"
                        :aria-label="$t('common.previousImage')"
                        :disabled="!canScrollThumbsPrev"
                        @click="scrollThumbs(-1)"
                      />
                      <div
                        ref="thumbsViewport"
                        class="product-gallery__thumbs-viewport"
                        role="tablist"
                        :aria-label="$t('common.productImages')"
                        @scroll.passive="updateThumbsNav"
                      >
                        <div ref="thumbsTrack" class="product-gallery__thumbs-track">
                          <button
                            v-for="(src, index) in product.galleryImages"
                            :key="'thumb-' + index + '-' + src"
                            type="button"
                            role="tab"
                            class="product-gallery__thumb"
                            :class="{ 'is-active': index === activeImageIndex }"
                            :aria-selected="index === activeImageIndex ? 'true' : 'false'"
                            :aria-label="galleryAlt(index)"
                            @click="selectImage(index)"
                          >
                            <img :src="src" :alt="galleryAlt(index)" />
                          </button>
                        </div>
                      </div>
                      <button
                        v-if="thumbsNeedNav"
                        type="button"
                        class="product-gallery__thumbs-nav product-gallery__thumbs-nav--next"
                        :aria-label="$t('common.nextImage')"
                        :disabled="!canScrollThumbsNext"
                        @click="scrollThumbs(1)"
                      />
                    </div>
                  </div>

                  <div v-else class="product-gallery product-gallery--empty">
                    <span>{{ productDisplayName }}</span>
                  </div>
                </div>

                <div class="product-detail-info">
                  <p v-if="productCategoryName" class="product-detail-category">
                    {{ productCategoryName }}
                  </p>
                  <h2 class="product-detail-title">{{ productDisplayName }}</h2>

                  <section class="product-detail-description">
                    <h3 class="product-detail-description__heading">
                      {{ $t('common.introduction') }}
                    </h3>
                    <div
                      v-if="descriptionParagraphs.length"
                      class="product-detail-description__body"
                    >
                      <p
                        v-for="(paragraph, index) in descriptionParagraphs"
                        :key="'desc-' + index"
                      >
                        <template
                          v-for="(line, lineIndex) in displayLines(paragraph)"
                          :key="lineIndex"
                        >
                          <br v-if="lineIndex > 0" />
                          {{ line }}
                        </template>
                      </p>
                    </div>
                    <p v-else class="product-detail-description__empty">
                      {{ $t('productDetail.noDescription') }}
                    </p>
                  </section>

                  <router-link :to="backToProductsLink" class="product-detail-back">
                    {{ $t('common.backToProducts') }}
                  </router-link>
                </div>
              </div>
            </article>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>

    <Teleport to="body">
      <div
        v-if="previewOpen"
        class="mmg-storefront product-image-preview"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('common.previewImage')"
        @click.self="closePreview"
      >
        <button
          type="button"
          class="product-image-preview__close"
          :aria-label="$t('common.closePreview')"
          @click="closePreview"
        />

        <button
          v-if="product && product.galleryImages.length > 1"
          type="button"
          class="product-image-preview__nav product-image-preview__nav--prev"
          :aria-label="$t('common.previousImage')"
          @click.stop="prevImage"
        />
        <button
          v-if="product && product.galleryImages.length > 1"
          type="button"
          class="product-image-preview__nav product-image-preview__nav--next"
          :aria-label="$t('common.nextImage')"
          @click.stop="nextImage"
        />

        <figure class="product-image-preview__figure" @click.stop>
          <img
            :src="activeImageSrc"
            :alt="galleryAlt(activeImageIndex)"
            class="product-image-preview__img"
          />
          <figcaption
            v-if="product && product.galleryImages.length > 1"
            class="product-image-preview__counter"
          >
            {{ activeImageIndex + 1 }} / {{ product.galleryImages.length }}
          </figcaption>
        </figure>
      </div>
    </Teleport>
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import { newsContentToParagraphs, newsParagraphToDisplayLines } from '@/api/news'
import { fetchPublicProduct, mapStorefrontProduct } from '@/api/products'

const THUMB_SCROLL_MIN = 5

export default {
  name: 'ProductDetailPage',
  components: { SiteHeader, SiteFooter },
  data() {
    return {
      product: null,
      activeImageIndex: 0,
      previewOpen: false,
      loading: true,
      loadError: '',
      thumbsNeedNav: false,
      canScrollThumbsPrev: false,
      canScrollThumbsNext: false
    }
  },
  computed: {
    productDisplayName() {
      void this.currentLocale
      return this.product ? this.$productName(this.product) : ''
    },
    productCategoryName() {
      void this.currentLocale
      return this.product ? this.$productCategoryName(this.product) : ''
    },
    activeImageSrc() {
      return this.product?.galleryImages?.[this.activeImageIndex] || ''
    },
    descriptionParagraphs() {
      return newsContentToParagraphs(this.product?.description)
    },
    backToProductsLink() {
      const slug = this.product?.categorySlug
      return slug ? { path: '/products', query: { category: slug } } : { path: '/products' }
    }
  },
  watch: {
    currentLocale() {
      this.updatePageTitle()
    },
    '$route.params.id': {
      immediate: true,
      handler() {
        this.loadProduct()
      }
    },
    previewOpen(open) {
      if (open) {
        document.body.classList.add('product-preview-open')
        document.addEventListener('keydown', this.onPreviewKeydown)
      } else {
        document.body.classList.remove('product-preview-open')
        document.removeEventListener('keydown', this.onPreviewKeydown)
      }
    }
  },
  mounted() {
    document.body.classList.add('product-detail')
    if (typeof ResizeObserver !== 'undefined') {
      this.thumbsResizeObserver = new ResizeObserver(() => this.updateThumbsNav())
    }
    window.addEventListener('resize', this.updateThumbsNav)
  },
  updated() {
    this.$nextTick(() => this.updateThumbsNav())
  },
  beforeUnmount() {
    document.body.classList.remove('product-detail')
    document.body.classList.remove('product-preview-open')
    document.removeEventListener('keydown', this.onPreviewKeydown)
    window.removeEventListener('resize', this.updateThumbsNav)
    this.thumbsResizeObserver?.disconnect()
  },
  methods: {
    displayLines(paragraph) {
      return newsParagraphToDisplayLines(paragraph)
    },
    updateThumbsNav() {
      const viewport = this.$refs.thumbsViewport
      if (!viewport) {
        this.thumbsNeedNav = false
        this.canScrollThumbsPrev = false
        this.canScrollThumbsNext = false
        return
      }

      const imageCount = this.product?.galleryImages?.length || 0
      const overflows = viewport.scrollWidth > viewport.clientWidth + 2
      this.thumbsNeedNav =
        imageCount >= THUMB_SCROLL_MIN || overflows

      const maxScroll = viewport.scrollWidth - viewport.clientWidth
      this.canScrollThumbsPrev = viewport.scrollLeft > 2
      this.canScrollThumbsNext = viewport.scrollLeft < maxScroll - 2

      if (this.thumbsResizeObserver && !this._thumbsObserved) {
        this.thumbsResizeObserver.observe(viewport)
        this._thumbsObserved = true
      }
    },
    scrollThumbs(direction) {
      const viewport = this.$refs.thumbsViewport
      if (!viewport) return
      const step = 86 * 4
      viewport.scrollBy({ left: direction * step, behavior: 'smooth' })
      window.setTimeout(() => this.updateThumbsNav(), 320)
    },
    scrollActiveThumbIntoView() {
      const viewport = this.$refs.thumbsViewport
      const track = this.$refs.thumbsTrack
      const thumb = track?.children?.[this.activeImageIndex]
      if (!thumb || !viewport) return
      const thumbLeft = thumb.offsetLeft
      const thumbRight = thumbLeft + thumb.offsetWidth
      const viewLeft = viewport.scrollLeft
      const viewRight = viewLeft + viewport.clientWidth
      if (thumbLeft < viewLeft) {
        viewport.scrollTo({ left: thumbLeft - 8, behavior: 'smooth' })
      } else if (thumbRight > viewRight) {
        viewport.scrollTo({ left: thumbRight - viewport.clientWidth + 8, behavior: 'smooth' })
      }
      window.setTimeout(() => this.updateThumbsNav(), 320)
    },
    galleryAlt(index) {
      const name = this.productDisplayName || this.$t('common.product')
      return `${name} — image ${index + 1}`
    },
    selectImage(index) {
      this.activeImageIndex = index
      this.$nextTick(() => this.scrollActiveThumbIntoView())
    },
    openPreview() {
      if (!this.activeImageSrc) return
      this.previewOpen = true
    },
    closePreview() {
      this.previewOpen = false
    },
    onPreviewKeydown(event) {
      if (!this.previewOpen) return
      if (event.key === 'Escape') {
        this.closePreview()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prevImage()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.nextImage()
      }
    },
    prevImage() {
      const len = this.product?.galleryImages?.length || 0
      if (len <= 1) return
      this.activeImageIndex = (this.activeImageIndex - 1 + len) % len
      this.$nextTick(() => this.scrollActiveThumbIntoView())
    },
    nextImage() {
      const len = this.product?.galleryImages?.length || 0
      if (len <= 1) return
      this.activeImageIndex = (this.activeImageIndex + 1) % len
      this.$nextTick(() => this.scrollActiveThumbIntoView())
    },
    async loadProduct() {
      const id = Number(this.$route.params.id)
      if (!id) {
        this.loadError = this.$t('productDetail.notFound')
        this.loading = false
        return
      }

      this.loading = true
      this.loadError = ''
      this.activeImageIndex = 0
      this.previewOpen = false
      try {
        const res = await fetchPublicProduct(id)
        this.product = mapStorefrontProduct(res.data)
        this.updatePageTitle()
      } catch (e) {
        this.product = null
        this.loadError = e.message || this.$t('productDetail.loadError')
      } finally {
        this.loading = false
        if (this._thumbsObserved) {
          this.thumbsResizeObserver?.disconnect()
          this._thumbsObserved = false
        }
        this.$nextTick(() => this.updateThumbsNav())
      }
    },
    updatePageTitle() {
      if (this.product) {
        document.title = `${this.$productName(this.product)} - Boris Bags`
      }
    }
  }
}
</script>

<style scoped>
.product-detail-content {
  width: 100%;
  padding: 20px 30px 48px;
  box-sizing: border-box;
}

.product-detail-status {
  margin: 0;
  text-align: center;
  color: var(--mmg-text, #747474);
  font-size: 17px;
}

.product-detail-article {
  max-width: 1180px;
  margin: 0 auto;
}

.product-detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 48px;
  align-items: start;
}

.product-detail-media {
  min-width: 0;
}

.product-detail-info {
  padding-top: 8px;
}

.product-detail-category {
  margin: 0 0 10px;
  font-size: 17px;
  color: var(--mmg-link-hover, #e7141a);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.product-detail-title {
  margin: 0 0 24px;
  font-size: 34px;
  font-weight: 400;
  line-height: 1.25;
  color: var(--mmg-heading, #333);
}

.product-detail-description {
  margin: 0 0 28px;
}

.product-detail-description__heading {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: var(--mmg-heading, #333);
}

.product-detail-description__body p {
  margin: 0 0 14px;
  font-size: 17px;
  line-height: 1.75;
  color: var(--mmg-text, #747474);
}

.product-detail-description__body p:last-child {
  margin-bottom: 0;
}

.product-detail-description__empty {
  margin: 0;
  font-size: 17px;
  line-height: 1.75;
  color: var(--mmg-text, #999);
}

.product-detail-back {
  display: inline-block;
  margin-top: 8px;
  font-size: 17px;
  color: var(--mmg-heading, #333);
  text-decoration: none;
}

.product-detail-back:hover {
  color: var(--mmg-link-hover, #e7141a);
}

@media (max-width: 900px) {
  .product-detail-layout {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .product-detail-info {
    padding-top: 0;
  }

  .product-detail-title {
    font-size: 28px;
  }
}
</style>
